/**
 * 発電チャートの hover tooltip (発電ダッシュボード / 発電BG詳細 で共有)。
 *
 * design も同じ形で、`js/charts.js` の `renderHatsuChart` 1 本を
 * 発電ダッシュボード (`js/hatsu-data.js`) と発電BG詳細
 * (`js/hatsu-detail-data.js:271`) の両方が呼んでいる。 需要側も既に
 * [[DemandTooltip]] を `RegionChart` (ダッシュボード) と `DetailChart` (詳細) で
 * 共有しており、 発電だけ詳細側に tooltip が無い状態だった (#7693 / #7064)。
 *
 * 行の型は**このコンポーネントが持つ** (`GenerationTooltipKoma`)。 page 側の
 * 行型 (`HatsuKomaData` / `HatsuDetailKomaData`) を共有部品が import すると
 * 依存が逆流するため、 呼出側が `satisfies` で満たす形にする
 * (需要側 `DemandTooltipKoma` と同じ扱い)。
 *
 * 内訳フィールドを `Partial<Record<GenSourceKey, ...>>` にしているのは、
 * 発電BG詳細の行 (`HatsuDetailKomaData`) が内訳フィールドを 1 つも持たない
 * ため (#2807 で値が入るまで plant 粒度の内訳は提供されない。詳細画面の
 * 積み上げ Bar は据え置き — #7537 のスコープ外)。 必須 `Record` にすると
 * 詳細画面の行が構造的にこの型を満たせず、共有できなくなる。
 */
import type { TFunction } from 'i18next'
import type { TooltipContentProps } from 'recharts'
import type { Payload } from 'recharts/types/component/DefaultTooltipContent'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { GENERATION_LIST_SERIES_COLORS } from '~/constants/chartPalette'
import type { GenSourceKey } from '~/constants/generationSource'
import { useGenerationSourceList } from '~/hooks/useGenerationSourceList'
import { badgeColors } from '~/models/badgeColors'
import { komaEndTime } from '~/models/komaToTime'

/**
 * tooltip が読む 1 コマ分の値。 発電ダッシュボード (`HatsuKomaData`) と
 * 発電BG詳細 (`HatsuDetailKomaData`) の**共通部分**だけを持つ。
 */
export type GenerationTooltipKoma = {
  time: string
  genPlan: number
} & Partial<Record<GenSourceKey, number | null>>

/**
 * バッジの並び順・色・ラベルは発電形式 9 区分の SoT
 * (`~/constants/generationSource`) からそのまま引く。 tooltip 独自の配列を
 * 持つと、9 区分化 (#7537) や積み上げ順変更のたびに 2 箇所を直す必要が
 * 生まれる。
 */
/* [#7058 / AC-SPS-007・008] 並び順と色は個人設定の永続値なので hook から取る。
   module スコープの const だと読み込み時に既定値で固まり、 tooltip のバッジだけ
   チャートの積み上げ順・色とズレる。 */

const Badge = ({
  color,
  label,
}: {
  color: string
  label: string
}): React.ReactElement => {
  // 地色・文字色とも系列色から導く。 文字色は design (readableOn) と同じ白/黒判定で、
  // それが AA 4.5:1 に届かない系列だけ地色を同色相のまま沈める (badgeColors 参照)。
  const badge = badgeColors(color)

  return (
    // fontSize (11px) + fontWeight (700) の組が TYPOGRAPHY_ROLES のどの役割とも
    // 一致しないため raw DOM + style={{}} のまま現状維持 (D-1 raw style 1本化)。
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        borderRadius: '3px',
        background: badge.background,
        // 文字色は**背景から決める**。 design (`js/charts.js:41-47` の
        // `readableOn`) が背景の YIQ 明度 (>= 150) で暗色 / 白を切り替えており、
        // `bfn()` (:1377) が `color: readableOn(color)` として使う。
        //
        // 固定白だと**太陽光バッジが読めない** — 地 `Brand.yellow` (#ffdd29) の
        // YIQ は 210 で、白文字とのコントラストは 1.3:1 しかない (#7064)。
        // 同じ helper を需要側の 2 画面 (`DemandTooltip` / `WeatherChart`) は
        // 既に使っており、 発電だけ取り残されていた。
        color: badge.color,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: '16px',
      }}
    >
      {label}
    </span>
  )
}

/**
 * hover tooltip の中身。 Recharts の tooltip は hover を起点に描くので
 * happy-dom からは開けない。 中身の文言と typography を固定するために
 * **単体で描けるよう export する** (需要側 `RegionChart` と同じ扱い)。
 */
export const GenerationTooltipContent = ({
  active,
  payload,
  t,
}: TooltipContentProps<number, string> & {
  t: TFunction
}): React.ReactElement | null => {
  /* hook は早期 return より前に置く (Rules of Hooks)。 */
  const sourceBadges = useGenerationSourceList()
  const typedPayload = payload as
    | ReadonlyArray<Payload<number, string>>
    | undefined
  if (!active || !typedPayload?.length) return null

  const row = (typedPayload[0] as { payload?: GenerationTooltipKoma })
    .payload as GenerationTooltipKoma
  const time = row.time
  const [h, m] = time.split(':').map(Number)
  const komaIndex = h * 2 + (m === 30 ? 1 : 0) + 1
  // コマ 48 の終わりは **24:00**。 `komaEndTime` の godoc:「コマ 48 は "24:00" で
  // 止め、"00:00" へ折り返さない。計画・実績・インバランスはすべて受渡日単位なので、
  // 翌日へ回ると別の受渡日のコマに読める」。 ここには `% 24` で 0 時に折り返す
  // ローカル実装があり、 コマ 48 が「23:30 – 00:00」 になっていた
  // (8080 `js/charts.js:1372-1374` も折り返さず 24:00 を出す)。
  const endTime = komaEndTime(time)
  /** 値が届いている発電形式だけ。 design `js/charts.js` の `bfn` (`val != null`) と同じ。 */
  const visibleSources = sourceBadges.flatMap((s) => {
    const val = row[s.key]
    return val != null ? [{ ...s, val }] : []
  })

  return (
    <Box
      minWidth="160px"
      padding="10px 14px"
      background="Surface.primary"
      borderWidth="1px"
      /* 枠線は DS の `Border.lowEmphasis` (#e0e0e0)。 design
         (`js/charts.js:1360` の `cssVar('border-low')`) と同じ。 旧実装は生 hex
         `#ddd` で design より濃かった (#7064)。 */
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      // 影は DS の `shadLg` (DemandTooltip と同じ。 生の rgba は DS の影と別物 = #7064)
      boxShadow="shadLg"
    >
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        gap="16px"
        marginBottom="8px"
      >
        {/* 見出しは 14px/700 (design `js/charts.js:1379`)。 14px = `md` なので
            token に載る (旧実装の 15px は DS スケール外だった)。 */}
        <span style={{ fontSize: fontSizes.md.value, fontWeight: 700 }}>
          {t('page:balanceGeneration.tooltip.koma')} {komaIndex}
        </span>
        {/* 時間レンジは 12px / low-emphasis (design `js/charts.js:1380` の
            `font-size:12px; color:htipMuted` = `--text-low-emphasis`)。
            12px = `sm`。 旧実装は `backLink` (13px = DS スケール外) + 生 hex
            `#666` (≒ mid-emphasis) で、 寸法と emphasis の両方がズレていた (#7064)。 */}
        <Text as="span" textStyle="smallText" color="Text.lowEmphasis">
          {time} – {endTime}
        </Text>
      </Flex>

      <Box marginBottom="6px">
        {/* セクション見出しは 12px・weight **600**・low-emphasis (design 同 :1381 の
            `htipMuted` = `--text-low-emphasis`)。 旧実装は 13px/700 + 既定色 (黒) で、
            値より目立っていた。 12px = `sm`。 */}
        <div
          style={{
            fontSize: fontSizes.sm.value,
            fontWeight: 600,
            color: LightModeSemanticColors.Text.lowEmphasis.value,
            marginBottom: '4px',
          }}
        >
          {t('page:balanceGeneration.tooltip.generation')}
        </div>
        <Flex alignItems="center" gap="6px">
          {/* 計画値は**種別ではない**ので専用色を持つ。 design
              (`js/charts.js:1382`) は `cssVar('surface-accent-primary')` を渡して
              おり、 これは同 repo の `GENERATION_LIST_SERIES_COLORS.genPlan` と
              同じ値。 以前は `BADGE_COLORS.hydro` を渡しており、 **同じ tooltip の
              中で「計画値」 と「水力」 が完全に同色**になっていた (issue #7064)。 */}
          <Badge
            color={GENERATION_LIST_SERIES_COLORS.genPlan}
            label={t('page:balanceGeneration.tooltip.planValue')}
          />
          {/* 値は 14px/700 (design 同 :1377 の `bfn`)。 計画値も発電形式の値も同じ。 */}
          <span
            style={{
              fontSize: fontSizes.md.value,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {row.genPlan.toLocaleString()}
          </span>
        </Flex>
      </Box>

      {/* 発電形式内訳。 **値が無い区分はバッジごと出さず、 全区分が無ければ
          セクションごと出さない**。 design も同じで、 `js/charts.js` の `bfn`
          (:1381) は `val != null` の時だけ span を返し、 `if (srcHtml)` が
          非空の時だけ「電源種別」 ブロック (旧実装、design 側の呼称) を足す。

          「–」 を並べると害がある: 発電BG詳細
          (`HatsuDetailKomaData`、`convertGenerationDetailResponse.ts`) は
          発電形式の内訳フィールドを**1 つも持たない** (内訳は plant 粒度でしか
          来ず、9 区分の値は #2807 で積み上げデータが整備されるまで未提供)。
          そのままだと詳細画面のツールチップに毎回「– – – – – – – – –」 の
          9 バッジが出てしまう。 「取れていない」 のではなく「0 に近い何か」 と
          読めてしまう。 */}
      {visibleSources.length > 0 && (
        <div>
          {/* セクション見出し (発電と同じ扱い)。 */}
          <div
            style={{
              fontSize: fontSizes.sm.value,
              fontWeight: 600,
              color: LightModeSemanticColors.Text.lowEmphasis.value,
              marginBottom: '4px',
            }}
          >
            {t('page:balanceGeneration.tooltip.sourceBreakdown')}
          </div>
          <Flex alignItems="center" flexWrap="wrap" gap="6px">
            {visibleSources.map(({ key, labelKey, color, val }) => (
              <Box
                key={key}
                as="span"
                alignItems="center"
                gap="3px"
                display="inline-flex"
              >
                <Badge color={color} label={t(labelKey)} />
                {/* 種別値も計画値と同じ 14px/700 (design 同 :1377)。 旧実装は
                    13px で、 同じ役割の 2 つが別サイズだった。 */}
                <span
                  style={{
                    fontSize: fontSizes.md.value,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {val.toLocaleString()}
                </span>
              </Box>
            ))}
          </Flex>
        </div>
      )}
    </Box>
  )
}
